import { ApiResponse, axiosInstance } from '@/lib/axios';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { QueryConfig } from '@/lib/react-query';
import { TGeneralCareNotification } from '../types/notification.type';

export type TGetGeneralCareNotificationResponse = ApiResponse<TGeneralCareNotification>;

export const getGeneralCareNotification = async (): Promise<TGetGeneralCareNotificationResponse> => {
  const url = `/notifications/general-care-configuration`;

  return axiosInstance.get(url);
};

export const GET_GENERAL_CARE_NOTIFICATION_QUERY_KEY = 'get-general-care-notification';

export const getGeneralCareNotificationQueryOptions = <TData = TGetGeneralCareNotificationResponse>(
  select?: (data: TGetGeneralCareNotificationResponse) => TData
) => {
  return queryOptions({
    queryKey: [GET_GENERAL_CARE_NOTIFICATION_QUERY_KEY],
    queryFn: () => getGeneralCareNotification(),
    select,
  });
};

// Overload when select is provided - returns transformed type
export function useGeneralCareNotificationQuery<TData>(options: {
  queryConfig?: QueryConfig<typeof getGeneralCareNotificationQueryOptions<TData>>;
  select: (data: TGetGeneralCareNotificationResponse) => TData;
}): ReturnType<typeof useQuery<TData>>;

// Overload when select is not provided - returns raw API response (even if generic is provided)
export function useGeneralCareNotificationQuery(options: {
  queryConfig?: QueryConfig<typeof getGeneralCareNotificationQueryOptions<TGetGeneralCareNotificationResponse>>;
}): ReturnType<typeof useQuery<TGetGeneralCareNotificationResponse>>;

// Implementation
export function useGeneralCareNotificationQuery<TData = TGetGeneralCareNotificationResponse>({
  queryConfig,
  select,
}: {
  queryConfig?: QueryConfig<typeof getGeneralCareNotificationQueryOptions<TData | TGetGeneralCareNotificationResponse>>;
  select?: (data: TGetGeneralCareNotificationResponse) => TData;
}) {
  return useQuery({
    ...getGeneralCareNotificationQueryOptions(select),
    ...queryConfig,
  }) as ReturnType<
    typeof useQuery<TData extends TGetGeneralCareNotificationResponse ? TGetGeneralCareNotificationResponse : TData>
  >;
}
